import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChecklistRtlIcon from "@mui/icons-material/ChecklistRtl";
import { useNavigate } from "react-router-dom";
import TopNavBar from "../components/TopNavBar";
import {
  createServicePlanTemplate,
  getServicePlanTemplates,
} from "../api/servicePlans";
import { getAllTaskMetadata } from "../api/listTypes";
import { getCustomers } from "../api/customer";

export default function ConfigureServicePlansPage() {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createSaving, setCreateSaving] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [createForm, setCreateForm] = useState({
    templateName: "",
    serviceTypeId: "",
    scope: "general",
    customerId: "",
    versionNo: 1,
  });

  useEffect(() => {
    Promise.all([
      getServicePlanTemplates(),
      getAllTaskMetadata(),
      getCustomers(),
    ])
      .then(([templateData, metadata, customersData]) => {
        setTemplates(templateData.templates || []);
        setServiceTypes(metadata?.taskMetaData?.service_types || []);
        setCustomers(
          Array.isArray(customersData)
            ? customersData
            : customersData?.customers || [],
        );
      })
      .catch((err) => setError(err.message || "Failed to load templates"))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    const templateName = String(createForm.templateName || "").trim();
    const serviceTypeId = Number(createForm.serviceTypeId);
    const versionNo = Number(createForm.versionNo || 1);

    if (!templateName) {
      setCreateError("Template name is required");
      return;
    }
    if (!serviceTypeId) {
      setCreateError("Service type is required");
      return;
    }
    if (createForm.scope === "customer" && !createForm.customerId) {
      setCreateError("Customer is required when scope is Customer");
      return;
    }

    try {
      setCreateError(null);
      setCreateSaving(true);

      const data = await createServicePlanTemplate({
        templateName,
        serviceTypeId,
        customerId:
          createForm.scope === "customer"
            ? Number(createForm.customerId)
            : null,
        versionNo,
        active: 1,
      });

      const created = data?.template;
      if (created?.pk_id) {
        setTemplates((prev) => [created, ...prev]);
        setCreateOpen(false);
        setCreateForm({
          templateName: "",
          serviceTypeId: "",
          scope: "general",
          customerId: "",
          versionNo: 1,
        });
        navigate(`/configure-service-plans/${created.pk_id}`);
      }
    } catch (err) {
      setCreateError(err.message || "Failed to create service plan template");
    } finally {
      setCreateSaving(false);
    }
  };

  return (
    <>
      <TopNavBar heading="Configure Service Plans" />
      <Container maxWidth="lg" sx={{ mt: 3, pb: 4 }}>
        <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1.5 }}>
          <ChecklistRtlIcon sx={{ color: "text.secondary" }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Service Plan Templates
          </Typography>
          <Box sx={{ ml: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                setCreateError(null);
                setCreateOpen(true);
              }}
            >
              Create Template
            </Button>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && templates.length === 0 && (
          <Typography color="text.secondary">
            No service plan templates found.
          </Typography>
        )}

        {!loading && !error && templates.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Template</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Service type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Version
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Tasks
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">
                    Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow
                    key={tpl.pk_id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/configure-service-plans/${tpl.pk_id}`)
                    }
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tpl.template_name}
                      </Typography>
                    </TableCell>
                    <TableCell>{tpl.customer_name || "General"}</TableCell>
                    <TableCell>{tpl.service_type_name || "-"}</TableCell>
                    <TableCell align="center">{tpl.version_no ?? 1}</TableCell>
                    <TableCell align="center">{tpl.tasks_count ?? 0}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tpl.active ? "Active" : "Inactive"}
                        size="small"
                        color={tpl.active ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      <Dialog
        open={createOpen}
        onClose={() => {
          if (!createSaving) setCreateOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Service Plan Template</DialogTitle>
        <DialogContent sx={{ pt: "8px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}

            <TextField
              label="Template Name"
              fullWidth
              value={createForm.templateName}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  templateName: e.target.value,
                }))
              }
            />

            <TextField
              select
              label="Service Type"
              fullWidth
              value={createForm.serviceTypeId}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  serviceTypeId: e.target.value,
                }))
              }
            >
              <MenuItem value="">Select Service Type</MenuItem>
              {serviceTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.name}
                </MenuItem>
              ))}
            </TextField>

            <FormControl>
              <FormLabel id="template-scope-label">Template Scope</FormLabel>
              <RadioGroup
                row
                aria-labelledby="template-scope-label"
                value={createForm.scope}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    scope: e.target.value,
                    customerId:
                      e.target.value === "general" ? "" : prev.customerId,
                  }))
                }
              >
                <FormControlLabel
                  value="general"
                  control={<Radio />}
                  label="General"
                />
                <FormControlLabel
                  value="customer"
                  control={<Radio />}
                  label="Customer"
                />
              </RadioGroup>
            </FormControl>

            {createForm.scope === "customer" && (
              <TextField
                select
                label="Customer"
                fullWidth
                value={createForm.customerId}
                onChange={(e) =>
                  setCreateForm((prev) => ({
                    ...prev,
                    customerId: e.target.value,
                  }))
                }
              >
                <MenuItem value="">Select Customer</MenuItem>
                {customers.map((customer) => (
                  <MenuItem key={customer.pk_id} value={customer.pk_id}>
                    {customer.uk_name || customer.name}
                  </MenuItem>
                ))}
              </TextField>
            )}

            <TextField
              label="Version"
              type="number"
              inputProps={{ min: 1 }}
              value={createForm.versionNo}
              onChange={(e) =>
                setCreateForm((prev) => ({
                  ...prev,
                  versionNo: e.target.value,
                }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCreateOpen(false)}
            disabled={createSaving}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createSaving}
          >
            {createSaving ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
